import AppSettings from "../../../../Settings/AppSettings";
import React, { useContext, useEffect, useState } from "react";
import { CategoryContext } from "../CategoryContext";
import ServiceWrapper from "../../../../Services/ServiceWrapper";
import AuthManager from "../../../../Common/AuthManager";
import { useNavigate } from "react-router";
import Accordion from "Components/DesignSystem/Accordion/Accordion";
import Alert from "Components/DesignSystem/Alert/Alert";
import EmptyState from "Components/DesignSystem/EmptyState/EmptyState";
import Spinner from "Components/DesignSystem/Spinner/Spinner";
import Table from "Components/DesignSystem/Table/Table";
import Typography from "Components/DesignSystem/Typography/Typography";
import { Refresh } from "Components/DesignSystem/Iconography/Icons";
import { FOCUS_RING } from "Components/DesignSystem/focusRing";

/**
 * Associated Rules — on the design system since the category module's Phase 5.
 * The service call, the lazy load-once gate, the id-change reset and — note —
 * this component's own lowercase "loading"/"success" statuses (it never shared
 * the twins' capitalised grammar) are the legacy logic verbatim. Rendering
 * changed: DS Accordion (controlled, § 192 actions slot for the refresh
 * button), the hand-built bootstrap table becomes DS Table, and the rule-name
 * cell — a clickable <th> with no keyboard access in the legacy — is now a
 * real button that navigates to /gms/rule/edit/:id.
 */
const AssociatedRules = () => {
  let api_base_url = AppSettings.GMS_BACKEND_API_URL;
  const navigate = useNavigate();
  const categoryTypeContextData = useContext(CategoryContext);
  const [loading, setLoading] = useState({
    status: "",
    message: "",
  });
  const [collapse, setCollapse] = useState(false);
  let [associatedRules, setAssociatedRules] = useState(null);

  async function load_associated_Rules() {
    setLoading({ status: "loading", message: "Loading Associated Rules" });
    let res = await ServiceWrapper.doGet(
      `${api_base_url}category/${categoryTypeContextData.editingItem.id}/associatedrules/list`,
      null,
      null,
      AuthManager.getGmsToken()
    );
    if (!res.errorfound) {
      setLoading({ status: "success", message: "" });
      setAssociatedRules(res.data.data);
    } else {
      setLoading({ status: "Error", message: "Error while loading details" });
    }
  }

  function handle_collapse() {
    setCollapse(!collapse);
  }

  useEffect(() => {
    setAssociatedRules(null);
    setLoading({
      status: "",
      message: "",
    });
  }, [categoryTypeContextData.editingItem.id]);

  useEffect(() => {
    if (associatedRules === null && collapse) {
      load_associated_Rules();
    }
  }, [collapse]);

  const columns = [
    {
      id: "name",
      header: "NAME / EFFECTIVE FROM",
      accessor: "name",
      width: "35%",
      cellType: "custom",
      render: (_value, row) => (
        <div className="tw-min-w-0">
          {/* TODO(design-system): icon/link-only button — no DS IconButton/link
              primitive yet; a real <button> replaces the legacy clickable <th>,
              which had no keyboard access at all. */}
          <button
            type="button"
            data-testid={`category-associated-rule-${row.id}-link`}
            className={`tw-bg-transparent tw-border-0 tw-p-0 tw-text-left tw-text-body-primary tw-text-accent hover:tw-underline tw-cursor-pointer ${FOCUS_RING}`}
            onClick={() => {
              navigate({
                pathname: `/gms/rule/edit/${row.id}`,
              });
            }}
          >
            {row.name}
          </button>
          {/* caption-metadata renders an inline <span>; without tw-block the
              timestamp glues onto the end of the name (user-flagged). */}
          <Typography
            variant="caption-metadata"
            className="tw-block tw-mt-0.5 tw-text-ink-tertiary"
            data-testid={`category-associated-rule-${row.id}-created-on`}
          >
            {row.created_on}
          </Typography>
        </div>
      ),
    },
    {
      id: "type",
      header: "TYPE",
      accessor: "type",
      width: "10%",
    },
    {
      id: "left_category_name",
      header: "CATEGORY 1",
      accessor: "left_category_name",
    },
    {
      id: "right_category_name",
      header: "CATEGORY 2",
      accessor: "right_category_name",
    },
    {
      id: "committed",
      header: "COMMITTED",
      accessor: (row) => (row.committed ? "Yes" : "No"),
    },
  ];

  /* Header-row action (§ 192): the legacy floated the refresh button OVER the
     header with zIndex + marginTop:-34px; same render condition, same handler. */
  const headerActions =
    collapse && loading.status === "success" ? (
      <button
        aria-label="Refresh"
        data-testid="category-associated-rules-btn"
        type="button"
        className={`tw-flex tw-items-center tw-justify-center tw-p-1.5 tw-rounded-ctl tw-border-0 tw-bg-transparent tw-text-ink-secondary hover:tw-text-accent hover:tw-bg-surface-hover tw-cursor-pointer ${FOCUS_RING}`}
        onClick={() => {
          load_associated_Rules();
        }}
      >
        <Refresh size={16} aria-hidden="true" />
      </button>
    ) : undefined;

  return (
    <Accordion
      title="Associated Rules"
      open={collapse}
      onOpenChange={() => handle_collapse()}
      actions={headerActions}
      data-testid="components-associatedrules-div-1"
      variant="boxed"
    >
      <div className="tw-pb-4">
        {loading.status === "loading" && (
          <Alert tone="info" live>
            <span className="tw-inline-flex tw-items-center tw-gap-2">
              <Spinner size="sm" aria-hidden="true" />
              <span>{loading.message}</span>
            </span>
          </Alert>
        )}

        {loading.status === "success" && (
          <>
            {associatedRules && associatedRules.length > 0 ? (
              <div className="tw-pt-3">
                <Table
                  columns={columns}
                  data={associatedRules}
                  rowKey="id"
                  hoverable
                  data-testid="category-associated-rules-table"
                />
              </div>
            ) : (
              <div className="tw-pt-3">
                <EmptyState size="sm" title="Associated Rules Not Found" />
              </div>
            )}
          </>
        )}
        {loading.status === "Error" && (
          <Alert tone="danger" live>
            {loading.message}
          </Alert>
        )}
      </div>
    </Accordion>
  );
};
export default AssociatedRules;
